import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { S3 } from "../../../../constants";
import Modal from "../../../../parts/modal-new";
import SvgIcon from "../../../../parts/SvgIcon";

const SIDES = ["front", "side", "back"];

const sanitizeName = (name) =>
  (name || "photos").replace(/[^\w\s-]/g, "_").trim() || "photos";

const formatPhotoDate = (utcDate) => {
  if (!utcDate) return null;
  return moment(utcDate, "YYYYMMDD").format("MMM D, YYYY");
};

const ChallengePhotoCompareModal = ({ participant, onClose }) => {
  const { t } = useTranslation("coaching-portal");
  const { bodyPhotos, name } = participant;
  const [zoomSide, setZoomSide] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  if (!bodyPhotos) return null;
  const { before, after } = bodyPhotos;
  before.date = 20260301;
  after.date = 20260508;

  const hasBefore = before && SIDES.some((s) => before[s]);
  const hasAfter = after && SIDES.some((s) => after[s]);

  if (!hasBefore && !hasAfter) return null;

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const { default: JSZip } = await import("jszip");
      const zip = new JSZip();
      const folderName = sanitizeName(name);
      const folder = zip.folder(folderName);

      const photos = [];
      for (const phase of ["before", "after"]) {
        const group = phase === "before" ? before : after;
        if (!group) continue;
        for (const side of SIDES) {
          if (group[side]) {
            photos.push({ phase, side, src: group[side] });
          }
        }
      }

      await Promise.all(
        photos.map(async ({ phase, side, src }) => {
          const url = S3.toCDN(src);
          const ext = url.split("?")[0].split(".").pop() || "jpg";
          const res = await fetch(url);
          if (!res.ok) throw new Error(`Failed to fetch ${url}`);
          const blob = await res.blob();
          folder.file(`${phase}-${side}.${ext}`, blob);
        })
      );

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(zipBlob);
      link.download = `${folderName}.zip`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error(error);
      alert(t("challengeReport.photoCompare.downloadFailed"));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Modal
      title={t("challengeReport.photoCompare.title", { name })}
      className="challenge-photo-compare"
      onClose={onClose}
      dontRefreshOnClose={true}
    >
      <div className="body">
        {!zoomSide ? (
          <>
            <header className="challenge-photo-compare__header">
              <span className="challenge-photo-compare__label">
                {t("challengeReport.photoCompare.before")}
              </span>
              <button
                data-button-type="secondary"
                data-icon
                onClick={handleDownload}
                disabled={isDownloading}
                type="button"
                className="challenge-photo-compare__download-btn"
              >
                <SvgIcon
                  src="/img/new-icons/arrow-forward.svg"
                  classNames="rotate-90 w-4"
                />
                {isDownloading
                  ? t("challengeReport.photoCompare.downloading")
                  : t("challengeReport.photoCompare.downloadAll")}
              </button>
              <span className="challenge-photo-compare__label">
                {t("challengeReport.photoCompare.after")}
              </span>
            </header>
            <div className="challenge-photo-compare__grid">
              {SIDES.map((side) => {
                const beforeSrc = before?.[side];
                const afterSrc = after?.[side];
                if (!beforeSrc && !afterSrc) return null;
                return (
                  <div className="challenge-photo-compare__row" key={side}>
                    <div className="challenge-photo-compare__photo-col">
                      <PhotoSlot src={beforeSrc} />
                      {before?.date && (
                        <span className="challenge-photo-compare__date">
                          {formatPhotoDate(before.date)}
                        </span>
                      )}
                    </div>
                    <button
                      data-button-type="secondary"
                      onClick={() => setZoomSide(side)}
                      className="challenge-photo-compare__zoom-btn"
                      type="button"
                    >
                      <SvgIcon src="/img/new-icons/Zoom.svg" />
                      {t(`challengeReport.photoCompare.${side}`)}
                    </button>
                    <div className="challenge-photo-compare__photo-col">
                      <PhotoSlot src={afterSrc} />
                      {after?.date && (
                        <span className="challenge-photo-compare__date">
                          {formatPhotoDate(after.date)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <>
            <header className="challenge-photo-compare__header">
              <div className="flex gap-2 items-center">
                <SvgIcon
                  src="/img/new-icons/back-button.svg"
                  onClick={() => setZoomSide(null)}
                />
                <span>{t(`challengeReport.photoCompare.${zoomSide}`)}</span>
              </div>
            </header>
            <div className="challenge-photo-compare__zoom">
              <div>
                <span className="challenge-photo-compare__label">
                  {t("challengeReport.photoCompare.before")}
                </span>
                <PhotoSlot src={before?.[zoomSide]} large />
                {before?.date && (
                  <span className="challenge-photo-compare__date">
                    {formatPhotoDate(before.date)}
                  </span>
                )}
              </div>
              <div>
                <span className="challenge-photo-compare__label">
                  {t("challengeReport.photoCompare.after")}
                </span>
                <PhotoSlot src={after?.[zoomSide]} large />
                {after?.date && (
                  <span className="challenge-photo-compare__date">
                    {formatPhotoDate(after.date)}
                  </span>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

const PhotoSlot = ({ src, large }) => {
  const { t } = useTranslation("coaching-portal");
  if (!src) {
    return (
      <div
        className={`challenge-photo-compare__placeholder ${large ? "challenge-photo-compare__placeholder--large" : ""}`}
      >
        {t("challengeReport.photoCompare.noPhoto")}
      </div>
    );
  }
  return (
    <img
      src={S3.toCDN(src)}
      className={`challenge-photo-compare__photo ${large ? "challenge-photo-compare__photo--large" : ""}`}
      width={large ? undefined : 240}
      height={large ? undefined : 320}
      alt={t("challengeReport.photoCompare.bodyAlt")}
    />
  );
};

export default ChallengePhotoCompareModal;
